import type {
    CategoryFilterState,
    CategoryTree,
} from '@rocco/ui/filter-sort/view-models/filter-state/category-filter-state';

const normaliseUrl = (url: string): string => url.replace(/\/+$/, '');

/**
 * Depth-first search for the node with the given canonical URL.
 */
export const findCategoryNode = (
    root: CategoryTree,
    url: string,
): CategoryTree | null => {
    if (normaliseUrl(root.url) === normaliseUrl(url)) {
        return root;
    }

    for (const child of root.children) {
        const found = findCategoryNode(child, url);
        if (found) return found;
    }

    return null;
};

/**
 * The ancestor chain `[root, …, node]` for the node with the given URL, or
 * an empty array when the URL is not in the tree.
 */
export const getCategoryTrail = (
    root: CategoryTree,
    url: string,
): CategoryTree[] => {
    if (normaliseUrl(root.url) === normaliseUrl(url)) {
        return [root];
    }

    for (const child of root.children) {
        const trail = getCategoryTrail(child, url);
        if (trail.length > 0) {
            return [root, ...trail];
        }
    }

    return [];
};

/**
 * Stage the category with the given URL as the pending selection. The root
 * node stages as an explicit selection too, so a commit emits the vertical
 * root path rather than falling back to the current page's path. Returns
 * null when the URL is not in the tree.
 */
export const stageCategorySelection = (
    state: CategoryFilterState,
    url: string,
): CategoryFilterState | null => {
    const node = findCategoryNode(state.category, url);
    if (!node) return null;

    return {
        ...state,
        selectedCategory: node,
        isRoot: normaliseUrl(node.url) === normaliseUrl(state.category.url),
    };
};
